import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { LinkButtonHref } from '~/features/navigation'
import { getConfigValue } from '~/helpers/envHelpers'
import { useTRPC } from '~/services/trpc'
import { SetupWizard } from '~/templates/wizard/presets/setup'

import { Button, Field, FieldError, Input, toast } from '@hap/components'

import { DISPLAY, ICON, providers } from '../-constants'
import { checkValidity } from './-utils'

export const Route = createFileRoute('/_public/signup/')({
  component: RouteComponent,
})

const schema = z.object({
  email: z.email(),
})

function RouteComponent() {
  const trpc = useTRPC()
  const navigate = Route.useNavigate()
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)
  const mutation = useMutation(trpc.signup.sendCode.mutationOptions())

  const form = useForm({
    defaultValues: {
      email: '',
    },
    validators: {
      onChange: schema,
    },
    onSubmit: async ({ value }) => {
      const normalizedEmail = value.email.toLowerCase()
      await mutation
        .mutateAsync({ email: normalizedEmail })
        .then(() => {
          navigate({ to: '/signup/code', search: { email: normalizedEmail } })
        })
        .catch((error) => {
          toast.error(error?.message || 'An unknown error occurred')
        })
    },
  })

  return (
    <SetupWizard title="Sign up for Happitu" currentStep={1}>
      <section className="grid gap-8 p-10">
        <header className="mx-auto mb-8 max-w-[400px] space-y-4 text-center">
          <h1 className="text-4xl font-bold">Tap into your conversations with Happitu</h1>
          <p className="text-xl">
            First, let's verify your identity. Enter your work email or continue with your
            Google or Microsoft workspace.
          </p>
        </header>

        <div className="grid gap-2">
          <div className="grid gap-2">
            {Object.entries(providers).map(([key]) => {
              const match = providers[key]
              if (match?.hide) return null

              const isLoading = loadingProvider === key
              const icon = ICON[key]

              return (
                <LinkButtonHref
                  key={key}
                  href={`${getConfigValue('VITE_AUTH_URL')}/auth/${key}/authorize?signup=1`}
                  className="flex items-center gap-2"
                  variant="secondary"
                  size="lg"
                  loading={isLoading}
                  loadingLabel={`Connecting to ${DISPLAY[key] || key}...`}
                  disabled={loadingProvider !== null && !isLoading}
                  onClick={(event) => {
                    if (
                      event.button !== 0 ||
                      event.metaKey ||
                      event.ctrlKey ||
                      event.shiftKey ||
                      event.altKey
                    ) {
                      return
                    }
                    setLoadingProvider(key)
                  }}
                >
                  {icon && <i className="h-[20px] w-[20px]">{icon}</i>}
                  {DISPLAY[key] || key}
                </LinkButtonHref>
              )
            })}
          </div>

          <div className="text-accent-foreground relative my-10 flex items-center border-t text-center">
            <span className="bg-accent absolute top-1/2 left-1/2 inline-block -translate-x-1/2 -translate-y-1/2 p-2">
              Or continue with
            </span>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void form.handleSubmit()
            }}
            className="grid space-y-4"
          >
            <form.Field
              name="email"
              children={(field) => {
                const isInvalid = checkValidity({ field, form })
                return (
                  <Field data-invalid={isInvalid}>
                    <Input
                      name={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      placeholder="Work Email"
                      aria-invalid={isInvalid}
                      variant="inverse"
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            />

            <Button
              type="submit"
              variant="lime"
              size="lg"
              loading={mutation.isPending}
              loadingLabel="Sending email..."
            >
              Continue
            </Button>
          </form>

          <div className="mt-4 flex justify-center">
            Already have an account?&nbsp;
            <Link to="/login" className="underline">
              Log in
            </Link>
          </div>
        </div>
      </section>
    </SetupWizard>
  )
}
